import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGame } from '../context/GameContext';
import * as api from '../api';

// ─── Constants ────────────────────────────────────────────────────────────────

const RESOURCE_OPTIONS = [
  // Layer 1 — Raw
  'iron_ore', 'copper_ore', 'titanium_ore', 'tungsten_ore', 'silicon',
  'rare_crystals', 'quantum_dust', 'ice', 'hydrogen_gas', 'methane_gas', 'carbon', 'biomass',
  // Layer 2 — Processed
  'iron_plate', 'copper_plate', 'titanium_plate', 'tungsten_plate', 'silicon_wafer',
  'crystal_core', 'quantum_substrate', 'water', 'refined_gas', 'polymer_base', 'bio_gel', 'carbon_block',
  // Layer 3 — Secondary
  'steel', 'heavy_alloy', 'reinforced_alloy', 'basic_circuit', 'advanced_circuit',
  'quantum_processor', 'fuel_cell', 'high_density_fuel', 'bio_fuel', 'industrial_polymer', 'composite_material',
  // Layer 4 — Components
  'hull_segment', 'structural_frame', 'engine_core', 'thruster', 'weapon_mount',
  'ai_core', 'control_unit', 'sensor_array', 'power_core', 'reactor_unit', 'shield_generator',
  // Layer 5 — Sub-assemblies
  'engine_module', 'hull_section', 'energy_system', 'ai_system',
  // Layer 6 — Ship items
  'ship_mining_item', 'ship_freighter_item', 'ship_capital_freighter_item', 'ship_cruiser_item', 'ship_battleship_item',
  // Legacy
  'energy', 'common_minerals', 'rare_elements', 'special_materials',
];

const RESOURCE_ICONS = {
  iron_ore: '⛏', copper_ore: '⛏', titanium_ore: '⛏', tungsten_ore: '⛏',
  silicon: '◇', rare_crystals: '◆', quantum_dust: '✦', ice: '❄',
  hydrogen_gas: '☁', methane_gas: '☁', carbon: '●', biomass: '🌿',
  iron_plate: '▬', copper_plate: '▬', titanium_plate: '▬', tungsten_plate: '▬',
  silicon_wafer: '◇', crystal_core: '◆', quantum_substrate: '✦', water: '💧',
  refined_gas: '⬡', polymer_base: '⬡', bio_gel: '⬡', carbon_block: '▪',
  steel: '◆', heavy_alloy: '◆', reinforced_alloy: '◆',
  basic_circuit: '⚡', advanced_circuit: '⚡', quantum_processor: '⚡',
  fuel_cell: '⬡', high_density_fuel: '⬡', bio_fuel: '⬡',
  industrial_polymer: '⬡', composite_material: '⬡',
  hull_segment: '⚙', structural_frame: '⚙', engine_core: '⚙', thruster: '⚙',
  weapon_mount: '⚙', ai_core: '⚙', control_unit: '⚙', sensor_array: '⚙',
  power_core: '⚙', reactor_unit: '⚙', shield_generator: '⚙',
  engine_module: '▣', hull_section: '▣', energy_system: '▣', ai_system: '▣',
  ship_mining_item: '🚀', ship_freighter_item: '🚀', ship_capital_freighter_item: '🚀',
  ship_cruiser_item: '🚀', ship_battleship_item: '🚀',
  credits: '₢', energy: '⚡', common_minerals: '⛏', rare_elements: '◆', special_materials: '✦',
};

// ─── Quick-sell client-side preview ──────────────────────────────────────────

function calcQuickSellPreview(resource, qty, buyOrders, baselinePrices) {
  if (!resource || !(qty > 0)) return null;
  const relevant = [...buyOrders]
    .filter(o => o.resource_type === resource)
    .sort((a, b) => Number(b.price_per_unit) - Number(a.price_per_unit));

  let remaining = qty;
  let totalCredits = 0;
  const steps = [];

  for (const order of relevant) {
    if (remaining <= 0) break;
    const fillQty = Math.min(remaining, order.quantity_remaining);
    const credits = Math.floor(fillQty * Number(order.price_per_unit));
    steps.push({ label: order.seller_type === 'player' ? 'Player Buy Order' : 'AI Buy Order', qty: fillQty, price: Number(order.price_per_unit), credits });
    totalCredits += credits;
    remaining -= fillQty;
  }
  if (remaining > 0 && baselinePrices[resource]) {
    const credits = Math.floor(remaining * baselinePrices[resource]);
    steps.push({ label: 'Baseline Floor', qty: remaining, price: baselinePrices[resource], credits });
    totalCredits += credits;
  }
  return { steps, totalCredits, remaining };
}

// ─── Group helper ─────────────────────────────────────────────────────────────

function groupListings(listings) {
  const groups = {};
  for (const l of listings) {
    const key = l.resource_type || 'unknown';
    if (!groups[key]) groups[key] = [];
    groups[key].push(l);
  }
  return groups;
}

// ─── Main Component ───────────────────────────────────────────────────────────

function MarketPanel() {
  const { updateInventory } = useGame();

  // Data
  const [sellListings, setSellListings] = useState([]);
  const [buyOrders, setBuyOrders] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [baselinePrices, setBaselinePrices] = useState({});
  const [autoSellRules, setAutoSellRules] = useState([]);

  // UI state
  const [tab, setTab] = useState('browse');
  const [browseType, setBrowseType] = useState('sell'); // 'sell' | 'buy'
  const [filter, setFilter] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState('info');

  // Quick Sell form
  const [sellResource, setSellResource] = useState('iron_ore');
  const [sellQty, setSellQty] = useState('');

  // Create Listing / Buy Order form
  const [orderType, setOrderType] = useState('sell'); // 'sell' | 'buy'
  const [listResource, setListResource] = useState('iron_ore');
  const [listQty, setListQty] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [listHours, setListHours] = useState('8');

  // Auto-sell rule editing
  const [asResource, setAsResource] = useState('iron_ore');
  const [asMinPrice, setAsMinPrice] = useState('');
  const [asMaxQty, setAsMaxQty] = useState('');
  const [editingRule, setEditingRule] = useState(null);
  const [editMinPrice, setEditMinPrice] = useState('');
  const [editMaxQty, setEditMaxQty] = useState('');

  const showMsg = (text, type = 'info') => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(null), 5000);
  };

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadMarket = useCallback(async () => {
    try {
      const resource = filter || undefined;
      const [sellRes, buyRes, priceRes, myRes] = await Promise.all([
        api.getListings({ resource, type: 'sell' }),
        api.getListings({ resource, type: 'buy' }),
        api.getBaselinePrices(),
        api.getMyListings(),
      ]);
      setSellListings(sellRes.data.listings || []);
      setBuyOrders(buyRes.data.listings || []);
      setBaselinePrices(priceRes.data.prices || {});
      setMyListings(myRes.data.listings || []);
    } catch (err) { console.error(err); }
  }, [filter]);

  const loadAutoSellRules = useCallback(async () => {
    try {
      const { data } = await api.getAutoSellRules();
      setAutoSellRules(data.rules || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadMarket(); }, [loadMarket]);
  useEffect(() => { loadAutoSellRules(); }, [loadAutoSellRules]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleQuickSell = async (e) => {
    e.preventDefault();
    const qty = Number(sellQty);
    if (!qty || qty <= 0) return showMsg('Enter a valid quantity', 'error');
    try {
      const { data } = await api.quickSell(sellResource, qty);
      if (data.inventory) updateInventory(data.inventory);
      const bd = data.breakdown || [];
      const playerFilled = bd.filter(s => s.type === 'player_buy_order').reduce((a, s) => a + s.qty, 0);
      const aiFilled = bd.filter(s => s.type === 'ai_buy_order').reduce((a, s) => a + s.qty, 0);
      const baselineFilled = bd.filter(s => s.type === 'baseline').reduce((a, s) => a + s.qty, 0);
      let detail = `+${data.credits_earned?.toLocaleString()} ₢`;
      if (playerFilled) detail += ` · ${playerFilled.toLocaleString()} to players`;
      if (aiFilled) detail += ` · ${aiFilled.toLocaleString()} to AI`;
      if (baselineFilled) detail += ` · ${baselineFilled.toLocaleString()} at floor`;
      showMsg(`Sold ${data.quantity_sold?.toLocaleString()} × ${sellResource.replace(/_/g, ' ')} — ${detail}`, 'success');
      setSellQty('');
      await loadMarket();
    } catch (err) { showMsg(err.response?.data?.error || 'Quick sell failed', 'error'); }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      if (orderType === 'sell') {
        const { data } = await api.createListing({
          resourceType: listResource, quantity: Number(listQty),
          pricePerUnit: Number(listPrice), expiresInHours: Number(listHours),
        });
        if (data.inventory) updateInventory(data.inventory);
        const matched = data.matched || 0;
        const msg2 = matched > 0
          ? `Sell listing posted — ${matched.toLocaleString()} instantly matched with buy orders`
          : 'Sell listing posted to market';
        showMsg(msg2, 'success');
      } else {
        const { data } = await api.createBuyOrder({
          resourceType: listResource, quantity: Number(listQty),
          pricePerUnit: Number(listPrice), expiresInHours: Number(listHours),
        });
        if (data.inventory) updateInventory(data.inventory);
        const matched = data.matched || 0;
        const msg3 = matched > 0
          ? `Buy order posted — ${matched.toLocaleString()} instantly matched with sell listings`
          : 'Buy order posted to market';
        showMsg(msg3, 'success');
      }
      setListQty(''); setListPrice('');
      await loadMarket();
    } catch (err) { showMsg(err.response?.data?.error || 'Order failed', 'error'); }
  };

  const handleBuyFromListing = async (listingId, qty) => {
    try {
      const { data } = await api.buyListing(listingId, qty);
      if (data.inventory) updateInventory(data.inventory);
      showMsg('Purchase complete', 'success');
      await loadMarket();
    } catch (err) { showMsg(err.response?.data?.error || 'Buy failed', 'error'); }
  };

  const handleCancel = async (listingId, listingType) => {
    try {
      const { data } = await api.cancelListing(listingId);
      if (data.inventory) updateInventory(data.inventory);
      const what = listingType === 'buy' ? 'Buy order cancelled — credits refunded' : 'Listing cancelled — resources returned';
      showMsg(what, 'success');
      await loadMarket();
    } catch (err) { showMsg(err.response?.data?.error || 'Cancel failed', 'error'); }
  };

  const handleAddAutoSellRule = async (e) => {
    e.preventDefault();
    try {
      await api.updateAutoSellRules([{
        resourceType: asResource, minPrice: Number(asMinPrice),
        maxQuantity: Number(asMaxQty) || null, isActive: true,
      }]);
      showMsg('Auto-sell rule saved', 'success');
      setAsMinPrice(''); setAsMaxQty('');
      await loadAutoSellRules();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleToggleAutoSell = async (rule) => {
    try {
      await api.updateAutoSellRules([{
        resourceType: rule.resource_type, minPrice: Number(rule.min_price),
        maxQuantity: rule.max_quantity ? Number(rule.max_quantity) : null,
        isActive: !rule.is_active,
      }]);
      await loadAutoSellRules();
    } catch (err) { showMsg(err.response?.data?.error || 'Toggle failed', 'error'); }
  };

  const handleDeleteAutoSell = async (resource) => {
    try {
      await api.deleteAutoSellRule(resource);
      showMsg('Rule deleted', 'success');
      if (editingRule === resource) setEditingRule(null);
      await loadAutoSellRules();
    } catch (err) { showMsg(err.response?.data?.error || 'Delete failed', 'error'); }
  };

  const handleSaveEditAutoSell = async (rule) => {
    try {
      await api.updateAutoSellRules([{
        resourceType: rule.resource_type, minPrice: Number(editMinPrice),
        maxQuantity: Number(editMaxQty) || null, isActive: rule.is_active,
      }]);
      showMsg('Rule updated', 'success');
      setEditingRule(null);
      await loadAutoSellRules();
    } catch (err) { showMsg(err.response?.data?.error || 'Update failed', 'error'); }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────

  const currentListings = browseType === 'buy' ? buyOrders : sellListings;
  const filteredListings = filter
    ? currentListings.filter(l => l.resource_type === filter)
    : currentListings;

  const groupedListings = useMemo(() => {
    const groups = groupListings(filteredListings);
    // Sort within each group
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) =>
        browseType === 'buy'
          ? Number(b.price_per_unit) - Number(a.price_per_unit) // buy orders: highest first
          : Number(a.price_per_unit) - Number(b.price_per_unit) // sell: cheapest first
      );
    }
    return groups;
  }, [filteredListings, browseType]);

  const quickSellPreview = useMemo(() =>
    calcQuickSellPreview(sellResource, Number(sellQty), buyOrders, baselinePrices),
    [sellResource, sellQty, buyOrders, baselinePrices]
  );

  const escrowNeeded = orderType === 'buy' && listQty && listPrice
    ? Math.ceil(Number(listQty) * Number(listPrice))
    : 0;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="panel market-panel">
      {msg && <div className={`hud-toast hud-toast-${msgType}`}>{msg}</div>}

      {/* Tab bar */}
      <div className="market-tabs">
        {[
          { key: 'browse', label: 'BROWSE' },
          { key: 'sell', label: 'QUICK SELL' },
          { key: 'create', label: 'CREATE ORDER' },
          { key: 'mine', label: `MY ORDERS${myListings.length > 0 ? ` (${myListings.length})` : ''}` },
          { key: 'auto', label: `AUTO-SELL${autoSellRules.length > 0 ? ` (${autoSellRules.length})` : ''}` },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`tab-btn ${tab === key ? 'active' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── BROWSE ─────────────────────────────────────────────────────────── */}
      {tab === 'browse' && (
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">
              <span className="title-accent">◈</span>
              {browseType === 'sell' ? ' SELL LISTINGS' : ' BUY ORDERS'}
            </h3>
            <div className="browse-controls">
              {/* Sell / Buy toggle */}
              <div className="browse-type-toggle">
                <button
                  className={`toggle-btn ${browseType === 'sell' ? 'active' : ''}`}
                  onClick={() => { setBrowseType('sell'); setExpandedGroup(null); }}
                >
                  SELL ORDERS
                </button>
                <button
                  className={`toggle-btn ${browseType === 'buy' ? 'active' : ''}`}
                  onClick={() => { setBrowseType('buy'); setExpandedGroup(null); }}
                >
                  BUY ORDERS
                </button>
              </div>
              <select
                className="hud-select"
                value={filter}
                onChange={e => { setFilter(e.target.value); setExpandedGroup(null); }}
              >
                <option value="">All Resources</option>
                {RESOURCE_OPTIONS.map(r => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
          </div>

          {browseType === 'sell' && (
            <p className="section-desc">
              Players and AI listing resources for sale. Click BUY to purchase at the listed price.
            </p>
          )}
          {browseType === 'buy' && (
            <p className="section-desc">
              Players and AI looking to buy resources. Use Quick Sell to fill the best available buy orders automatically.
            </p>
          )}

          {Object.keys(groupedListings).length === 0 && (
            <p className="hud-empty">No active {browseType === 'buy' ? 'buy orders' : 'listings'}.</p>
          )}

          <div className="market-groups">
            {Object.entries(groupedListings).map(([resourceType, items]) => {
              const isExpanded = expandedGroup === resourceType;
              const topItem = items[0];
              const totalQty = items.reduce((s, l) => s + Number(l.quantity_remaining), 0);
              const icon = RESOURCE_ICONS[resourceType] || '◈';

              return (
                <div key={resourceType} className={`market-group ${isExpanded ? 'expanded' : ''}`}>
                  <div className="market-group-row">
                    <div className="mg-resource">
                      <span className="mg-icon">{icon}</span>
                      <span className="mg-name">{resourceType.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="mg-stats">
                      <span className="mg-total-qty">{totalQty.toLocaleString()} units</span>
                      <span className="mg-best-price">
                        {browseType === 'buy' ? 'Best bid: ' : 'Best ask: '}
                        {Number(topItem.price_per_unit).toFixed(2)} ₢
                      </span>
                      <span className={`seller-badge ${topItem.seller_type}`}>
                        {topItem.seller_type === 'ai' ? 'AI' : 'PLAYER'}
                      </span>
                    </div>
                    <div className="mg-actions">
                      {browseType === 'sell' ? (
                        <button
                          className="btn btn-sm btn-buy"
                          onClick={() => handleBuyFromListing(topItem.id, topItem.quantity_remaining)}
                        >
                          BUY {Number(topItem.quantity_remaining).toLocaleString()}
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-sell"
                          onClick={() => { setTab('sell'); setSellResource(resourceType); }}
                        >
                          QUICK SELL
                        </button>
                      )}
                      {items.length > 1 && (
                        <button
                          className="btn btn-sm btn-ghost mg-expand-btn"
                          onClick={() => setExpandedGroup(isExpanded ? null : resourceType)}
                        >
                          {isExpanded ? '▲ Less' : `▼ ${items.length - 1} more`}
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && items.length > 1 && (
                    <div className="mg-dropdown">
                      {items.slice(1).map(l => (
                        <div key={l.id} className="mg-dropdown-row">
                          <span className="mg-dd-qty">{Number(l.quantity_remaining).toLocaleString()}</span>
                          <span className="mg-dd-price">{Number(l.price_per_unit).toFixed(2)} ₢/ea</span>
                          <span className={`seller-badge ${l.seller_type}`}>
                            {l.seller_type === 'ai' ? 'AI' : 'PLAYER'}
                          </span>
                          {browseType === 'sell' && (
                            <button className="btn btn-sm btn-buy" onClick={() => handleBuyFromListing(l.id, l.quantity_remaining)}>
                              BUY
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── QUICK SELL ──────────────────────────────────────────────────────── */}
      {tab === 'sell' && (
        <section className="section">
          <h3 className="section-title"><span className="title-accent">₢</span> QUICK SELL</h3>
          <p className="section-desc">
            Sells immediately at the best available price. Fills player buy orders first,
            then AI buy orders, then the baseline floor price for any remainder.
          </p>

          <form className="market-form" onSubmit={handleQuickSell}>
            <div className="form-group">
              <label className="hud-label">Resource</label>
              <select className="hud-select" value={sellResource} onChange={e => setSellResource(e.target.value)}>
                {RESOURCE_OPTIONS.map(r => (
                  <option key={r} value={r}>
                    {r.replace(/_/g, ' ')} — floor: {baselinePrices[r] || '?'} ₢
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="hud-label">Quantity</label>
              <input
                className="hud-input"
                type="number" min="1" placeholder="Amount"
                value={sellQty}
                onChange={e => setSellQty(e.target.value)}
                required
              />
            </div>

            {/* Real-time breakdown preview */}
            {quickSellPreview && quickSellPreview.steps.length > 0 && (
              <div className="quick-sell-preview">
                <div className="qsp-header">Expected return</div>
                {quickSellPreview.steps.map((step, i) => (
                  <div key={i} className="qsp-row">
                    <span className={`qsp-label qsp-${step.label.includes('Player') ? 'player' : step.label.includes('AI') ? 'ai' : 'baseline'}`}>
                      {step.label}
                    </span>
                    <span className="qsp-qty">{step.qty.toLocaleString()} units</span>
                    <span className="qsp-price">@ {step.price.toFixed(2)} ₢</span>
                    <span className="qsp-credits">= {step.credits.toLocaleString()} ₢</span>
                  </div>
                ))}
                <div className="qsp-total">
                  Total: <strong>{quickSellPreview.totalCredits.toLocaleString()} ₢</strong>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-glow">SELL NOW</button>
          </form>
        </section>
      )}

      {/* ── CREATE ORDER ─────────────────────────────────────────────────────── */}
      {tab === 'create' && (
        <section className="section">
          <h3 className="section-title">
            <span className="title-accent">◈</span>
            {orderType === 'sell' ? ' CREATE SELL LISTING' : ' CREATE BUY ORDER'}
          </h3>

          {/* Order type toggle */}
          <div className="order-type-toggle">
            <button
              className={`toggle-btn ${orderType === 'sell' ? 'active' : ''}`}
              onClick={() => setOrderType('sell')}
            >
              SELL LISTING
            </button>
            <button
              className={`toggle-btn ${orderType === 'buy' ? 'active' : ''}`}
              onClick={() => setOrderType('buy')}
            >
              BUY ORDER
            </button>
          </div>

          {orderType === 'sell' ? (
            <p className="section-desc">
              Post resources for sale at a fixed price. Resources are reserved from your inventory.
              Price must be above the baseline floor. New listings auto-match any open buy orders instantly.
            </p>
          ) : (
            <p className="section-desc">
              Post a request to buy resources at your max bid price. Credits are escrowed immediately.
              New buy orders auto-match any open sell listings at or below your bid.
              Cancel at any time to get unused credits back.
            </p>
          )}

          <form className="market-form" onSubmit={handleCreateOrder}>
            <div className="form-group">
              <label className="hud-label">Resource</label>
              <select className="hud-select" value={listResource} onChange={e => setListResource(e.target.value)}>
                {RESOURCE_OPTIONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="hud-label">Quantity</label>
                <input
                  className="hud-input" type="number" min="1" placeholder="Amount"
                  value={listQty} onChange={e => setListQty(e.target.value)} required
                />
              </div>
              <div className="form-group">
                <label className="hud-label">
                  {orderType === 'sell' ? 'Price / Unit' : 'Max Bid / Unit'}
                </label>
                <input
                  className="hud-input" type="number" min="0.01" step="0.01"
                  placeholder="₢ per unit"
                  value={listPrice} onChange={e => setListPrice(e.target.value)} required
                />
              </div>
              <div className="form-group">
                <label className="hud-label">Expires (hours)</label>
                <input
                  className="hud-input" type="number" min="1" max="24" placeholder="1–24"
                  value={listHours} onChange={e => setListHours(e.target.value)}
                />
              </div>
            </div>

            {orderType === 'sell' && listQty && listPrice && baselinePrices[listResource] && (
              <div className="sell-preview">
                If filled: <strong>{Math.floor(Number(listQty) * Number(listPrice)).toLocaleString()} ₢</strong>
                {' '}· Baseline floor: {baselinePrices[listResource]?.toFixed(2)} ₢/unit
              </div>
            )}

            {orderType === 'buy' && escrowNeeded > 0 && (
              <div className="sell-preview">
                Credits to escrow: <strong>{escrowNeeded.toLocaleString()} ₢</strong>
                {' '}(refunded if unfilled on cancel/expiry)
              </div>
            )}

            <button type="submit" className="btn btn-glow">
              {orderType === 'sell' ? 'POST SELL LISTING' : 'POST BUY ORDER'}
            </button>
          </form>
        </section>
      )}

      {/* ── MY ORDERS ─────────────────────────────────────────────────────────── */}
      {tab === 'mine' && (
        <section className="section">
          <h3 className="section-title"><span className="title-accent">◈</span> MY ACTIVE ORDERS</h3>
          {myListings.length === 0 && <p className="hud-empty">You have no active listings or buy orders.</p>}
          {myListings.length > 0 && (
            <div className="hud-table-wrap">
              <table className="hud-table">
                <thead>
                  <tr>
                    <th>TYPE</th>
                    <th>RESOURCE</th>
                    <th>QTY LEFT</th>
                    <th>PRICE/UNIT</th>
                    <th>EXPIRES</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {myListings.map(l => {
                    const expiresIn = Math.max(0, Math.round((new Date(l.expires_at) - Date.now()) / 60000));
                    const expHrs = Math.floor(expiresIn / 60);
                    const expMins = expiresIn % 60;
                    const isBuy = l.listing_type === 'buy';
                    return (
                      <tr key={l.id}>
                        <td>
                          <span className={`order-type-badge ${isBuy ? 'buy' : 'sell'}`}>
                            {isBuy ? 'BUY' : 'SELL'}
                          </span>
                        </td>
                        <td><span className="resource-tag">{l.resource_type?.replace(/_/g, ' ')}</span></td>
                        <td>{Number(l.quantity_remaining).toLocaleString()}</td>
                        <td className="price-cell">
                          {Number(l.price_per_unit).toFixed(2)} ₢
                          {isBuy && <span className="price-hint"> (bid)</span>}
                        </td>
                        <td>{expHrs > 0 ? `${expHrs}h ${expMins}m` : `${expMins}m`}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-ghost"
                            onClick={() => handleCancel(l.id, l.listing_type)}
                          >
                            CANCEL
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── AUTO-SELL ──────────────────────────────────────────────────────────── */}
      {tab === 'auto' && (
        <section className="section">
          <h3 className="section-title"><span className="title-accent">⚙</span> AUTO-SELL RULES</h3>
          <p className="section-desc">
            Rules run every 15 minutes. When a matching buy order appears above your minimum price,
            your resources are automatically sold into it — player buy orders before AI.
          </p>

          {autoSellRules.length > 0 && (
            <div className="hud-table-wrap" style={{ marginBottom: '1.5rem' }}>
              <table className="hud-table">
                <thead>
                  <tr>
                    <th>RESOURCE</th>
                    <th>MIN PRICE</th>
                    <th>MAX QTY</th>
                    <th>STATUS</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {autoSellRules.map(r => {
                    const isEditing = editingRule === r.resource_type;
                    return (
                      <tr key={r.resource_type}>
                        <td><span className="resource-tag">{r.resource_type?.replace(/_/g, ' ')}</span></td>
                        <td className="price-cell">
                          {isEditing
                            ? <input className="hud-input hud-input-sm" type="number" min="0.01" step="0.01" value={editMinPrice} onChange={e => setEditMinPrice(e.target.value)} />
                            : <>{Number(r.min_price).toFixed(2)} ₢</>}
                        </td>
                        <td>
                          {isEditing
                            ? <input className="hud-input hud-input-sm" type="number" min="1" placeholder="∞" value={editMaxQty} onChange={e => setEditMaxQty(e.target.value)} />
                            : <>{r.max_quantity ? Number(r.max_quantity).toLocaleString() : '∞'}</>}
                        </td>
                        <td>
                          <button
                            className={`btn btn-sm ${r.is_active ? 'btn-active-toggle' : 'btn-ghost'}`}
                            onClick={() => handleToggleAutoSell(r)}
                            disabled={isEditing}
                          >
                            {r.is_active ? 'ACTIVE' : 'PAUSED'}
                          </button>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {isEditing ? (
                            <>
                              <button className="btn btn-sm btn-success" onClick={() => handleSaveEditAutoSell(r)}>SAVE</button>
                              {' '}
                              <button className="btn btn-sm btn-ghost" onClick={() => setEditingRule(null)}>CANCEL</button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-sm btn-secondary" onClick={() => {
                                setEditingRule(r.resource_type);
                                setEditMinPrice(Number(r.min_price).toFixed(2));
                                setEditMaxQty(r.max_quantity ? String(r.max_quantity) : '');
                              }}>EDIT</button>
                              {' '}
                              <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAutoSell(r.resource_type)}>DELETE</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <form className="market-form" onSubmit={handleAddAutoSellRule}>
            <div className="form-group">
              <label className="hud-label">Resource</label>
              <select className="hud-select" value={asResource} onChange={e => setAsResource(e.target.value)}>
                {RESOURCE_OPTIONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="hud-label">Min Price / Unit</label>
                <input
                  className="hud-input" type="number" min="0.01" step="0.01"
                  placeholder="₢ minimum" value={asMinPrice}
                  onChange={e => setAsMinPrice(e.target.value)} required
                />
              </div>
              <div className="form-group">
                <label className="hud-label">Max Qty (optional)</label>
                <input
                  className="hud-input" type="number" min="1"
                  placeholder="Leave blank = unlimited" value={asMaxQty}
                  onChange={e => setAsMaxQty(e.target.value)}
                />
              </div>
            </div>
            <button type="submit" className="btn btn-glow">ADD RULE</button>
          </form>
        </section>
      )}
    </div>
  );
}

export default MarketPanel;
